import React, { useState, useEffect } from 'react'
import Button from 'devextreme-react/button'
import { Popup } from 'devextreme-react/popup'
import { LoadIndicator } from 'devextreme-react/load-indicator'
import UploadField from '../upload-field/uploadField'
import { TimestampDisplay } from '../../components'
import { editData, renderUrl } from '../../utils/model-data'
import { canEditField } from '../../utils/permissions'
import { useAuth } from '../../contexts/auth'
import axios from 'axios'

export default function MultipleUpload(props: any) {
	const { onHide, pKey, folder, model, name, values, settings, canEdit } = props
	const { user } = useAuth()

	const allowEditing = canEdit ?? canEditField(user, model, name)

	const keyValue = Array.isArray(pKey) ? pKey.map((k: any) => values[k]) : values[pKey]

	const [loading, setLoading] = useState(true)
	const [updating, setUpdating] = useState(false)

	const subfolderKeys = settings.subfolderFields !== undefined ? settings.subfolderFields : pKey
	const folderParts = [settings.folder]
	if (Array.isArray(subfolderKeys)) {
		for (let i = 0; i < subfolderKeys.length; i++) {
			folderParts.push(values[subfolderKeys[i]])
		}
	} else {
		folderParts.push(values[subfolderKeys])
	}

	const extensions = settings.extensions ?? []

	const [files, setFiles] = useState<string[]>([])

	useEffect(() => {
		if (keyValue) {
			const id = Array.isArray(keyValue) ? encodeURIComponent(btoa(JSON.stringify(keyValue))) : keyValue
			axios
				.get(`${API_FIBER_URL}/view/row/${id}?folder=${folder}&model=${model}`)
				.then(response => response.data)
				.then(data => {
					if (data.values[name]) {
						setFiles(data.values[name].split('\n') || [])
					}
					setLoading(false)
				})
		}
	}, [folder, model, keyValue])

	settings.required = true

	const updateFiles = (newFiles: string[]) => {
		const fieldValue: any = {}
		// Remove duplicates from newFiles
		const uniqueFiles = Array.from(new Set(newFiles))
		fieldValue[name] = uniqueFiles.join('\n')
		setFiles(uniqueFiles)
		editData('view', { key: keyValue, values: fieldValue, folder, model }).then((data: any) => {
			setUpdating(false)
		})
	}

	const encodeToBase64 = (value: string) => {
		try {
			return btoa(value)
		} catch {
			// Fallback for non-Latin1 strings
			return btoa(unescape(encodeURIComponent(value)))
		}
	}

	const FilesList = (props: any) => {
		const { files } = props
		return (
			<div>
				<ul>
					{files
						.filter((file: string) => file.trim() !== '')
						.map((file: string) => {
							const [filePath = file, timestamp] = file.split('=>').map((part: string) => part.trim())
							const parsedTimestamp = Number(timestamp)
							const hasValidTimestamp = timestamp && !Number.isNaN(parsedTimestamp)
							return (
								<li key={file}>
									{renderUrl(filePath, 0, false)}{' '}
									{hasValidTimestamp && (
										<span style={{ display: 'inline-flex', alignItems: 'center' }}>
											(<TimestampDisplay timestamp={parsedTimestamp} />)
										</span>
									)}
									{allowEditing && (
										<Button
											icon="clear"
											hint="Unlink file"
											disabled={false}
											onClick={async () => {
												setUpdating(true)
												const encodedFile = encodeURIComponent(encodeToBase64(filePath))
												axios
													.delete(`${API_FIBER_URL}/view/file/${encodedFile}`)
													.then((data: any) => {
														const newFiles = files.filter((f: string) => f !== file)
														updateFiles(newFiles)
													})
											}}
											style={{ zoom: 0.6, marginLeft: '10px' }}
										/>
									)}
								</li>
							)
						})}
				</ul>
			</div>
		)
	}

	const renderContent = () => {
		if (loading) {
			return (
				<div className={'content-block'}>
					<LoadIndicator height={20} width={20} />
				</div>
			)
		}

		return (
			<div>
				{updating && (
					<div style={{ float: 'right', marginRight: '10px' }}>
						<LoadIndicator height={20} width={20} />
					</div>
				)}
				Folder: <strong>{folderParts.join('/')}</strong>
				{extensions && extensions.length > 0 && (
					<>
						<br />
						<i>
							Extensions: <strong>{extensions.join(', ')}</strong>
						</i>
					</>
				)}
				<FilesList files={files} />
				{allowEditing && (
					<UploadField
						folder={folder}
						model={model}
						name={name}
						multiple={true}
						uploadFolder={folderParts.join('/')}
						setValue={(value: any) => {
							const timestamp = Math.floor(Date.now() / 1000)
							updateFiles([...files, `${value}=>${timestamp}`])
						}}
						onStartUpload={() => {
							setUpdating(true)
						}}
						settings={settings}
					/>
				)}
			</div>
		)
	}

	return (
		<Popup
			visible={true}
			dragEnabled={true}
			resizeEnabled={true}
			showCloseButton={true}
			contentRender={renderContent}
			title={`Upload Files for ${name}`}
			position="center"
			hideOnOutsideClick={() => {
				return true
			}}
			onHiding={onHide}
		/>
	)
}
